import { Badge, AppState } from "@/types";
import { LucideIcon } from "lucide-react";
import {
  Hand,
  Sparkles,
  Target,
  Unlock,
  CheckCircle,
  Handshake,
  Headphones,
  Star,
  Key,
  Coins,
  DollarSign,
  Flame,
  Dumbbell,
  Medal,
  Cat,
  Sprout,
  Hash,
  Diamond,
  Crown,
  Sparkle,
  Zap,
  Rocket,
  Trophy,
  Briefcase,
  BarChart3,
  GraduationCap,
  PartyPopper,
  Theater,
  BookOpen,
  Users,
  Palette,
  ShieldCheck,
  HeartHandshake,
  LineChart,
  Cog,
  Bot,
  Sun,
  Award,
} from "lucide-react";

// Badge icon mapping
export const BADGE_ICONS: Record<string, LucideIcon> = {
  'welcome': Hand,
  'first-activity': Sparkles,
  'first-set': Target,
  'opening-master': Unlock,
  'closing-champion': CheckCircle,
  'networking-ninja': Handshake,
  'client-whisperer': Headphones,
  'referral-rockstar': Star,
  'service-superstar': Sparkles,
  'opportunity-creator': Key,
  'deal-maker': Handshake,
  'sales-machine': Coins,
  'revenue-generator': DollarSign,
  'streak-3': Flame,
  'week-warrior': Dumbbell,
  'month-champion': Medal,
  'streak-beast': Cat,
  'first-25': Sprout,
  'half-century': Hash,
  'century-club': Sparkles,
  'high-achiever': Star,
  'point-master': Diamond,
  'legend': Crown,
  'elite-performer': Sparkle,
  'goal-crusher': Zap,
  'overachiever': Rocket,
  'weekly-champion': Trophy,
  'consistency-king': Crown,
  'perfectionist': Sparkles,
  'junior-consultant': Briefcase,
  'senior-consultant': BarChart3,
  'principal-consultant': GraduationCap,
  'partner-level': Diamond,
  'team-player': Handshake,
  'culture-champion': PartyPopper,
  'jack-of-all-trades': Theater,
  'story-teller': BookOpen,
  'role-master': Users,
  'diversified-performer': Palette,
  'knowledge-seeker': GraduationCap,
  'skill-builder': Target,
  'client-advocate': ShieldCheck,
  'relationship-builder': HeartHandshake,
  'growth-catalyst': LineChart,
  'grind-master': Cog,
  'productivity-machine': Bot,
  'weekend-warrior': Sun,
  'milestone-hunter': Trophy,
  'achievement-collector': Award,
  'challenge-starter': Target,
  'challenge-master': Medal,
  'challenge-legend': Trophy,
};

// Streamlined sales-focused badges with working progress tracking
export const ALL_BADGES: Badge[] = [
  // === WELCOME & FIRST STEPS ===
  { id: 'welcome', name: 'Welcome!', description: 'Welcome to your journey! Start logging activities to unlock more badges.', imageUrl: '', icon: BADGE_ICONS['welcome'], rarity: 'common', target: 0, pointsThreshold: 0 },
  { id: 'first-activity', name: 'First Steps', description: 'Log your first activity of any type', imageUrl: '', icon: BADGE_ICONS['first-activity'], rarity: 'common', target: 1, pointsThreshold: 0 },
  { id: 'first-set', name: 'First Set', description: 'Log your first Set activity', imageUrl: '', icon: BADGE_ICONS['first-set'], rarity: 'common', target: 1, pointsThreshold: 0 },
  
  // === SALES ACTIVITY BADGES ===
  { id: 'opening-master', name: 'Opening Master', description: 'Complete 10 Opening activities', imageUrl: '', icon: BADGE_ICONS['opening-master'], rarity: 'rare', target: 10, pointsThreshold: 0 },
  { id: 'closing-champion', name: 'Closing Champion', description: 'Complete 25 Closing/Closed activities', imageUrl: '', icon: BADGE_ICONS['closing-champion'], rarity: 'epic', target: 25, pointsThreshold: 0 },
  { id: 'networking-ninja', name: 'Networking Ninja', description: 'Complete 10 Referral activities', imageUrl: '', icon: BADGE_ICONS['networking-ninja'], rarity: 'rare', target: 10, pointsThreshold: 0 },
  { id: 'client-whisperer', name: 'Client Whisperer', description: 'Complete 15 Client Servicing activities', imageUrl: '', icon: BADGE_ICONS['client-whisperer'], rarity: 'rare', target: 15, pointsThreshold: 0 },
  { id: 'referral-rockstar', name: 'Referral Rockstar', description: 'Complete 25 Referral activities', imageUrl: '', icon: BADGE_ICONS['referral-rockstar'], rarity: 'epic', target: 25, pointsThreshold: 0 },
  { id: 'service-superstar', name: 'Service Superstar', description: 'Complete 30 Client Servicing activities', imageUrl: '', icon: BADGE_ICONS['service-superstar'], rarity: 'epic', target: 30, pointsThreshold: 0 },
  { id: 'opportunity-creator', name: 'Opportunity Creator', description: 'Complete 50 Opening activities', imageUrl: '', icon: BADGE_ICONS['opportunity-creator'], rarity: 'epic', target: 50, pointsThreshold: 0 },
  
  // === DEAL CLOSING BADGES ===
  { id: 'deal-maker', name: 'Deal Maker', description: 'Close 10 deals (Closed activities)', imageUrl: '', icon: BADGE_ICONS['deal-maker'], rarity: 'rare', target: 10, pointsThreshold: 0 },
  { id: 'sales-machine', name: 'Sales Machine', description: 'Close 25 deals (Closed activities)', imageUrl: '', icon: BADGE_ICONS['sales-machine'], rarity: 'epic', target: 25, pointsThreshold: 0 },
  { id: 'revenue-generator', name: 'Revenue Generator', description: 'Close 50 deals (Closed activities)', imageUrl: '', icon: BADGE_ICONS['revenue-generator'], rarity: 'legendary', target: 50, pointsThreshold: 0 },
  
  // === STREAK BADGES ===
  { id: 'streak-3', name: '3-Day Streak', description: 'Maintain a 3-day activity streak', imageUrl: '', icon: BADGE_ICONS['streak-3'], rarity: 'common', target: 3, pointsThreshold: 0 },
  { id: 'week-warrior', name: 'Week Warrior', description: 'Maintain a 7-day activity streak', imageUrl: '', icon: BADGE_ICONS['week-warrior'], rarity: 'rare', target: 7, pointsThreshold: 0 },
  { id: 'month-champion', name: 'Month Champion', description: 'Maintain a 30-day activity streak', imageUrl: '', icon: BADGE_ICONS['month-champion'], rarity: 'legendary', target: 30, pointsThreshold: 0 },
  { id: 'streak-beast', name: 'Streak Beast', description: 'Maintain a 50-day activity streak', imageUrl: '', icon: BADGE_ICONS['streak-beast'], rarity: 'legendary', target: 50, pointsThreshold: 0 },
  
  // === POINT MILESTONES ===
  { id: 'first-25', name: 'Getting Started', description: 'Earn your first 25 points', imageUrl: '', icon: BADGE_ICONS['first-25'], rarity: 'common', target: 25, pointsThreshold: 25 },
  { id: 'half-century', name: 'Half Century', description: 'Earn 50 total points', imageUrl: '', icon: BADGE_ICONS['half-century'], rarity: 'common', target: 50, pointsThreshold: 50 },
  { id: 'century-club', name: 'Century Club', description: 'Earn 100 total points', imageUrl: '', icon: BADGE_ICONS['century-club'], rarity: 'rare', target: 100, pointsThreshold: 100 },
  { id: 'high-achiever', name: 'High Achiever', description: 'Earn 250 total points', imageUrl: '', icon: BADGE_ICONS['high-achiever'], rarity: 'rare', target: 250, pointsThreshold: 250 },
  { id: 'point-master', name: 'Point Master', description: 'Earn 500 total points', imageUrl: '', icon: BADGE_ICONS['point-master'], rarity: 'epic', target: 500, pointsThreshold: 500 },
  { id: 'legend', name: 'Legend', description: 'Earn 1000 total points', imageUrl: '', icon: BADGE_ICONS['legend'], rarity: 'legendary', target: 1000, pointsThreshold: 1000 },
  { id: 'elite-performer', name: 'Elite Performer', description: 'Earn 2000 total points', imageUrl: '', icon: BADGE_ICONS['elite-performer'], rarity: 'legendary', target: 2000, pointsThreshold: 2000 },
  
  // === WEEKLY GOAL BADGES ===
  { id: 'goal-crusher', name: 'Goal Crusher', description: 'Exceed weekly goal by 50%', imageUrl: '', icon: BADGE_ICONS['goal-crusher'], rarity: 'rare', target: 1, pointsThreshold: 0 },
  { id: 'overachiever', name: 'Overachiever', description: 'Double your weekly goal', imageUrl: '', icon: BADGE_ICONS['overachiever'], rarity: 'epic', target: 1, pointsThreshold: 0 },
  { id: 'weekly-champion', name: 'Weekly Champion', description: 'Achieve weekly goal 10 times', imageUrl: '', icon: BADGE_ICONS['weekly-champion'], rarity: 'epic', target: 10, pointsThreshold: 0 },
  { id: 'consistency-king', name: 'Consistency King', description: 'Meet weekly goal 5 weeks in a row', imageUrl: '', icon: BADGE_ICONS['consistency-king'], rarity: 'legendary', target: 5, pointsThreshold: 0 },
  { id: 'perfectionist', name: 'Perfectionist', description: 'Achieve weekly goal 5 weeks in a row', imageUrl: '', icon: BADGE_ICONS['perfectionist'], rarity: 'legendary', target: 5, pointsThreshold: 0 },
  
  // === PROFESSIONAL LEVEL BADGES ===
  { id: 'junior-consultant', name: 'Junior Consultant', description: 'Complete 50 total activities', imageUrl: '', icon: BADGE_ICONS['junior-consultant'], rarity: 'common', target: 50, pointsThreshold: 0 },
  { id: 'senior-consultant', name: 'Senior Consultant', description: 'Complete 150 total activities', imageUrl: '', icon: BADGE_ICONS['senior-consultant'], rarity: 'rare', target: 150, pointsThreshold: 0 },
  { id: 'principal-consultant', name: 'Principal Consultant', description: 'Complete 300 total activities', imageUrl: '', icon: BADGE_ICONS['principal-consultant'], rarity: 'epic', target: 300, pointsThreshold: 0 },
  { id: 'partner-level', name: 'Partner Level', description: 'Complete 500 total activities', imageUrl: '', icon: BADGE_ICONS['partner-level'], rarity: 'legendary', target: 500, pointsThreshold: 0 },
  
  // === TEAM & CULTURE BADGES ===
  { id: 'team-player', name: 'Team Player', description: 'Complete 10 Hangout activities', imageUrl: '', icon: BADGE_ICONS['team-player'], rarity: 'rare', target: 10, pointsThreshold: 0 },
  { id: 'culture-champion', name: 'Culture Champion', description: 'Complete 25 Hangout activities', imageUrl: '', icon: BADGE_ICONS['culture-champion'], rarity: 'epic', target: 25, pointsThreshold: 0 },
  
  // === VARIETY & MASTERY BADGES ===
  { id: 'jack-of-all-trades', name: 'Jack of All Trades', description: 'Log at least one of each activity type', imageUrl: '', icon: BADGE_ICONS['jack-of-all-trades'], rarity: 'rare', target: 1, pointsThreshold: 0 },
  { id: 'story-teller', name: 'Story Teller', description: 'Complete 15 Story activities', imageUrl: '', icon: BADGE_ICONS['story-teller'], rarity: 'rare', target: 15, pointsThreshold: 0 },
  { id: 'role-master', name: 'Role Master', description: 'Complete 20 Roleplay activities', imageUrl: '', icon: BADGE_ICONS['role-master'], rarity: 'rare', target: 20, pointsThreshold: 0 },
  { id: 'diversified-performer', name: 'Diversified Performer', description: 'Complete 5+ of each activity type', imageUrl: '', icon: BADGE_ICONS['diversified-performer'], rarity: 'epic', target: 1, pointsThreshold: 0 },
  { id: 'knowledge-seeker', name: 'Knowledge Seeker', description: 'Complete 30 Story activities', imageUrl: '', icon: BADGE_ICONS['knowledge-seeker'], rarity: 'epic', target: 30, pointsThreshold: 0 },
  { id: 'skill-builder', name: 'Skill Builder', description: 'Complete 40 Roleplay activities', imageUrl: '', icon: BADGE_ICONS['skill-builder'], rarity: 'epic', target: 40, pointsThreshold: 0 },
  
  // === CLIENT-FOCUSED BADGES ===
  { id: 'client-advocate', name: 'Client Advocate', description: 'Complete 20 Client Servicing activities', imageUrl: '', icon: BADGE_ICONS['client-advocate'], rarity: 'rare', target: 20, pointsThreshold: 0 },
  { id: 'relationship-builder', name: 'Relationship Builder', description: 'Complete 15 combined Referral and Client Servicing', imageUrl: '', icon: BADGE_ICONS['relationship-builder'], rarity: 'rare', target: 15, pointsThreshold: 0 },
  { id: 'growth-catalyst', name: 'Growth Catalyst', description: 'Complete 50 combined Referral and Client Servicing', imageUrl: '', icon: BADGE_ICONS['growth-catalyst'], rarity: 'epic', target: 50, pointsThreshold: 0 },
  
  // === PRODUCTIVITY BADGES ===
  { id: 'grind-master', name: 'Grind Master', description: 'Log 20 activities in a single day', imageUrl: '', icon: BADGE_ICONS['grind-master'], rarity: 'epic', target: 20, pointsThreshold: 0 },
  { id: 'productivity-machine', name: 'Productivity Machine', description: 'Log 30 activities in a single day', imageUrl: '', icon: BADGE_ICONS['productivity-machine'], rarity: 'legendary', target: 30, pointsThreshold: 0 },
  { id: 'weekend-warrior', name: 'Weekend Warrior', description: 'Log 10 activities on weekends', imageUrl: '', icon: BADGE_ICONS['weekend-warrior'], rarity: 'rare', target: 10, pointsThreshold: 0 },
  
  // === META ACHIEVEMENT BADGES ===
  { id: 'milestone-hunter', name: 'Milestone Hunter', description: 'Unlock 10 different badges', imageUrl: '', icon: BADGE_ICONS['milestone-hunter'], rarity: 'epic', target: 10, pointsThreshold: 0 },
  { id: 'achievement-collector', name: 'Achievement Collector', description: 'Unlock 25 different badges', imageUrl: '', icon: BADGE_ICONS['achievement-collector'], rarity: 'legendary', target: 25, pointsThreshold: 0 },
  
  // === CHALLENGE BADGES ===
  { id: 'challenge-starter', name: 'Challenge Starter', description: 'Complete your first challenge', imageUrl: '', icon: BADGE_ICONS['challenge-starter'], rarity: 'common', target: 1, pointsThreshold: 0 },
  { id: 'challenge-master', name: 'Challenge Master', description: 'Complete 10 challenges', imageUrl: '', icon: BADGE_ICONS['challenge-master'], rarity: 'rare', target: 10, pointsThreshold: 0 },
  { id: 'challenge-legend', name: 'Challenge Legend', description: 'Complete 25 challenges', imageUrl: '', icon: BADGE_ICONS['challenge-legend'], rarity: 'epic', target: 25, pointsThreshold: 0 },
];

export function checkBadgeProgress(badge: Badge, state: AppState): Badge {
  let progress = 0;
  let isUnlocked = false;
  
  const totalActivities = Object.values(state.activityCounts || {}).reduce((sum, count) => sum + count, 0);
  
  switch (badge.id) {
    // === WELCOME & FIRST STEPS ===
    case 'welcome':
      progress = state.isAuthenticated ? 1 : 0;
      isUnlocked = state.isAuthenticated;
      break;
    case 'first-activity':
      progress = totalActivities;
      break;
    case 'first-set':
      progress = state.activityCounts['Set'] || 0;
      break;
      
    // === SALES ACTIVITY BADGES ===
    case 'opening-master':
    case 'opportunity-creator':
      progress = state.activityCounts['Opening'] || 0;
      break;
    case 'closing-champion':
      progress = (state.activityCounts['Closed'] || 0) + (state.activityCounts['Closing'] || 0);
      break;
    case 'networking-ninja':
    case 'referral-rockstar':
      progress = state.activityCounts['Referral'] || 0;
      break;
    case 'client-whisperer':
    case 'service-superstar':
    case 'client-advocate':
      progress = state.activityCounts['Client Servicing'] || 0;
      break;
      
    // === DEAL CLOSING BADGES ===
    case 'deal-maker':
    case 'sales-machine':
    case 'revenue-generator':
      progress = state.activityCounts['Closed'] || 0;
      break;
      
    // === STREAK BADGES ===
    case 'streak-3':
    case 'week-warrior':
    case 'month-champion':
    case 'streak-beast':
      progress = state.streak || 0;
      break;
      
    // === POINT MILESTONES ===
    case 'first-25':
    case 'half-century':
    case 'century-club':
    case 'high-achiever':
    case 'point-master':
    case 'legend':
    case 'elite-performer':
      progress = state.totalPoints || 0;
      break;
      
    // === WEEKLY GOAL BADGES ===
    case 'goal-crusher':
      progress = state.currentWeekPoints >= (state.weeklyGoal * 1.5) ? 1 : 0;
      break;
    case 'overachiever':
      progress = state.currentWeekPoints >= (state.weeklyGoal * 2) ? 1 : 0;
      break;
    case 'weekly-champion':
      // Estimate based on streak and whether current week goal achieved
      progress = state.weeklyGoalStreak + (state.lastWeekGoalAchieved ? 1 : 0);
      break;
    case 'consistency-king':
    case 'perfectionist':
      progress = state.weeklyGoalStreak || 0;
      break;
      
    // === PROFESSIONAL LEVEL BADGES ===
    case 'junior-consultant':
    case 'senior-consultant':
    case 'principal-consultant':
    case 'partner-level':
      progress = totalActivities;
      break;
      
    // === TEAM & CULTURE BADGES ===
    case 'team-player':
    case 'culture-champion':
      progress = state.activityCounts['Hangout'] || 0;
      break;
      
    // === VARIETY & MASTERY BADGES ===
    case 'jack-of-all-trades':
      const activityTypes = ['Set', 'Opening', 'Closing', 'Closed', 'Hangout', 'Story', 'Roleplay', 'Referral', 'Client Servicing'];
      const typesCompleted = activityTypes.filter(type => (state.activityCounts[type] || 0) > 0).length;
      progress = typesCompleted >= activityTypes.length ? 1 : 0;
      break;
    case 'story-teller':
    case 'knowledge-seeker':
      progress = state.activityCounts['Story'] || 0;
      break;
    case 'role-master':
    case 'skill-builder':
      progress = state.activityCounts['Roleplay'] || 0;
      break;
    case 'diversified-performer':
      const allTypes = ['Set', 'Opening', 'Closing', 'Closed', 'Hangout', 'Story', 'Roleplay', 'Referral', 'Client Servicing'];
      const allHave5 = allTypes.filter(type => (state.activityCounts[type] || 0) >= 5).length >= allTypes.length;
      progress = allHave5 ? 1 : 0;
      break;
      
    // === CLIENT-FOCUSED BADGES ===
    case 'relationship-builder':
    case 'growth-catalyst':
      progress = (state.activityCounts['Referral'] || 0) + (state.activityCounts['Client Servicing'] || 0);
      break;
      
    // === PRODUCTIVITY BADGES ===
    case 'grind-master':
    case 'productivity-machine':
      progress = state.activitiesLoggedToday || 0;
      break;
    case 'weekend-warrior':
      // Count weekend activities from recent activities
      const weekendCount = (state.activities || []).filter(a => {
        const day = new Date(a.timestamp).getDay();
        return day === 0 || day === 6; // Sunday or Saturday
      }).length;
      progress = weekendCount;
      break;
      
    // === META ACHIEVEMENT BADGES ===
    case 'milestone-hunter':
    case 'achievement-collector':
      progress = (state.badges || []).filter(b => b.unlockedAt).length;
      break;
      
    // === CHALLENGE BADGES ===
    case 'challenge-starter':
    case 'challenge-master':
    case 'challenge-legend':
      // Count completed challenges
      const completedChallenges = (state.challenges || []).filter(c => c.status === 'completed').length;
      progress = completedChallenges;
      break;
  }
  
  // Check if badge should be unlocked
  if (!isUnlocked && badge.pointsThreshold > 0) {
    isUnlocked = (state.totalPoints || 0) >= badge.pointsThreshold;
  } else if (!isUnlocked) {
    isUnlocked = progress >= badge.target;
  }

  return {
    ...badge,
    progress: Math.min(progress, badge.target),
    unlockedAt: isUnlocked ? (badge.unlockedAt || new Date()) : undefined,
  };
}

export const getAllBadges = (state: AppState): Badge[] => {
  if (!state || !state.badges) {
    return ALL_BADGES.map(badge => checkBadgeProgress(badge, state || {} as AppState));
  }
  return ALL_BADGES.map(badge => {
    const existingBadge = state.badges.find(b => b.id === badge.id);
    return existingBadge || checkBadgeProgress(badge, state);
  });
};
